/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import Checkbox from '@mui/material/Checkbox';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import BellOutlinedIcon from 'components/icons/BellOutlinedIcon';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import notificationServices from 'services/notificationServices';
import { httpStatusOf, smsApi, SmsParticipantDto } from 'services/camMirrorService';
import { useAppSelector } from 'app/hooks';
import { checkEnableParseJSON } from 'components/helper/helper';
import { USER_ROLE } from 'constants/common';
import { routers } from 'router/router';
import { canUseSms, SMS_MIN_PLAN_LABEL } from 'utils/smsPlanGate';

interface Props { }

const SettingsNotification = (props: Props) => {
  const personalStore = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const stripeSubscriptionInfo = useAppSelector((store) => store.basicInformation.stripeSubscriptionInfo);
  const stripeSubscriptionInfoLoaded = useAppSelector(
    (store) => store.basicInformation.stripeSubscriptionInfoLoaded
  );
  const history = useHistory();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [data, setData] = React.useState<any>();
  const [saving, setSaving] = React.useState<boolean>(false);
  const [upgradeOpen, setUpgradeOpen] = React.useState<boolean>(false);
  // HEADER-DOCS-024: server-side SMS profile (cam-mirror). This is the source of truth
  // for the In-App SMS row; `smsProfileStatus` lets the row avoid flashing while loading
  // and fall back to the client-side plan check if the endpoint is unavailable.
  const [smsProfile, setSmsProfile] = React.useState<SmsParticipantDto | null>(null);
  const [smsProfileStatus, setSmsProfileStatus] = React.useState<'loading' | 'ok' | 'error'>('loading');
  const [smsSaving, setSmsSaving] = React.useState<boolean>(false);
  const userId = personalStore?.student_id;
  const userRole = personalStore?.user_role ?? sessionStorage.getItem('userRole');

  // HEADER-DOCS-024: "In-App Messaging SMS" (chat → SMS sync) is a paid feature.
  // Plans belong to STUDENTS only, so only a student on GradMap Starter is locked;
  // parents/counselors own no plan and keep the toggle (the chat itself follows the
  // student's plan, enforced by the backend gateway). Used only as a FALLBACK when
  // the cam-mirror SMS profile cannot be loaded.
  const isSmsLockedByPlan =
    userRole === USER_ROLE.student &&
    stripeSubscriptionInfoLoaded &&
    !canUseSms(stripeSubscriptionInfo?.subcription_plan);

  const getData = () => {
    if (userId) {
      notificationServices.getNotification(userId).then((res: any) => {
        setData(res);
        setLoading(false);
      });
      smsApi
        .getParticipant(userId)
        .then((profile) => {
          setSmsProfile(profile);
          setSmsProfileStatus('ok');
        })
        .catch(() => {
          setSmsProfile(null);
          setSmsProfileStatus('error');
        });
    }
  };

  const saveInAppSms = async (enabled: boolean) => {
    if (smsSaving) return;
    if (enabled && smsProfile && !smsProfile.smsAllowed) {
      setUpgradeOpen(true);
      return;
    }
    const previous = smsProfile?.smsReceiveEnabled ?? false;
    setSmsProfile((current) => (current ? { ...current, smsReceiveEnabled: enabled } : current));
    setSmsSaving(true);
    try {
      const next = await smsApi.setPreference(enabled);
      setSmsProfile(next);
      toast.success(enabled ? 'In-app SMS is on' : 'In-app SMS is off');
      getData();
    } catch (err) {
      setSmsProfile((current) => (current ? { ...current, smsReceiveEnabled: previous } : current));
      if (httpStatusOf(err) === 403) setUpgradeOpen(true);
    } finally {
      setSmsSaving(false);
    }
  };

  useEffect(() => {
    getData();
  }, [userId]);

  const getEmailNotificationStatus = (
    userType: string,
    notificationType?: string,
    source?: string
  ) => {
    return (
      checkEnableParseJSON(data?.email_notification_array, [
        {
          noti_type: 'email',
          is_enabled: false,
        },
        {
          noti_type: 'sms',
          is_enabled: false,
        },
        {
          noti_type: 'email',
          is_enabled: false,
          type: "receive"
        },
        {
          noti_type: 'sms',
          is_enabled: false,
          type: "receive"
        },
      ])?.find((item: any) =>
        item.noti_type === userType &&
        (notificationType ? item.type === notificationType : !item.type) &&
        (source ? item.source === source : !item.source)
      )?.is_enabled || false
    );
  };

  const updateEmailNotificationStatus = (
    userType: string,
    isEnabled: boolean,
    notificationType?: string,
    source?: string
  ) => {
    const toastId = toast.loading('Saving...');

    // Default array with all notification types
    const defaultArray = [
      {
        noti_type: 'email',
        is_enabled: false,
      },
      {
        noti_type: 'sms',
        is_enabled: false,
      },
      {
        noti_type: 'email',
        is_enabled: false,
        type: "receive",
        source: "in-app"
      },
      {
        noti_type: 'sms',
        is_enabled: false,
        type: "receive",
        source: "in-app"
      },
      {
        noti_type: 'email',
        is_enabled: false,
        type: "receive",
        source: "gradmap"
      },
      {
        noti_type: 'sms',
        is_enabled: false,
        type: "receive",
        source: "gradmap"
      },
    ];

    let emailArray = data?.email_notification_array
      ? JSON.parse(data?.email_notification_array)
      : defaultArray;

    // Ensure all required notification types exist in the array
    const requiredTypes = [
      { noti_type: 'email', type: undefined },
      { noti_type: 'sms', type: undefined },
      { noti_type: 'email', type: 'receive', source: 'in-app' },
      { noti_type: 'sms', type: 'receive', source: 'in-app' },
      { noti_type: 'email', type: 'receive', source: 'gradmap' },
      { noti_type: 'sms', type: 'receive', source: 'gradmap' },
    ];

    // Add missing notification types with default values
    requiredTypes.forEach(({ noti_type, type, source }) => {
      const exists = emailArray.some((item: any) =>
        item.noti_type === noti_type &&
        (type ? item.type === type : !item.type) &&
        (source ? item.source === source : !item.source)
      );
      if (!exists) {
        emailArray.push({
          noti_type,
          is_enabled: false,
          ...(type && { type }),
          ...(source && { source }),
        });
      }
    });

    const updatedArray = emailArray.map((item: any) => {
      if (
        item.noti_type === userType &&
        (notificationType ? item.type === notificationType : !item.type) &&
        (source ? item.source === source : !item.source)
      ) {
        return { ...item, is_enabled: isEnabled };
      }
      return item;
    });

    const dataUpdate = {
      ...data,
      email_notification_array: JSON.stringify(updatedArray),
      email: personalStore?.email,
      user_id: userId,
    };

    // HEADER-DOCS-024: optimistic update — flip the control immediately, roll back
    // to the previous settings if the save fails. Error toasts (incl. 429 rate
    // limit) are already shown by the shared http interceptor, so none is added here.
    const previousData = data;
    setData({ ...data, email_notification_array: JSON.stringify(updatedArray) });
    setSaving(true);

    notificationServices
      .updateNotification(dataUpdate)
      .then((res: any) => {
        if (res) {
          toast.success('Update notification status successfully');
          getData();
        }
      })
      .catch(() => {
        setData(previousData);
      })
      .finally(() => {
        toast.dismiss(toastId);
        setSaving(false);
      });
  };

  const goToPlans = () => {
    setUpgradeOpen(false);
    history.push(`${routers.settings.path}?active-tab=5`);
  };

  return !loading ? (
    <div>
      <div>
        {/* <div className="tab-panel2-content">
          <div className="row">
            <div className="col-md-12">
              <div className="d-flex align-items-center">
                <div
                  className="text-primary p-2 rounded-circle bg-primary-light d-flex align-items-center justify-content-center"
                  style={{ width: '35px', height: '35px', marginRight: '8px' }}
                >
                  <BellOutlinedIcon className="text-primray-light" width={'18px'} height={'18px'} />
                </div>
                <div className="text-primary fs-4 fw-bold">Send Notifications</div>
              </div>
            </div>
          </div>
        </div> */}

        {/* <Row className="mt-4">
          <Col xs={12} className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <TableContainer component={'div'}>
                  <Table sx={{ minWidth: 650 }} aria-label="send notifications table">
                    <TableHead>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ backgroundColor: '#FCFBFC' }}>
                        <TableCell
                          component="th"
                          scope="row"
                          className="text-field-mid fs-16px pl-30px"
                        >
                          Email Notifications
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={getEmailNotificationStatus('email')}
                            onChange={(_: any, checked: boolean) =>
                              updateEmailNotificationStatus('email', checked)
                            }
                          />
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#FCFBFC' }}>
                        <TableCell
                          component="th"
                          scope="row"
                          className="text-field-mid fs-16px pl-30px"
                        >
                          SMS Notifications
                        </TableCell>

                        <TableCell align="center">
                          <Checkbox
                            checked={getEmailNotificationStatus('sms')}
                            onChange={(_: any, checked: boolean) =>
                              updateEmailNotificationStatus('sms', checked)
                            }
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row> */}

        {/* <div className="tab-panel2-content mt-4"> */}
        <Row className="">
          <Col xs={12} className="mt-2">
            <div className="bg-white p-2">
              <div className="d-flex align-items-center">
                <div
                  className="text-primary p-2 rounded-circle bg-primary-light d-flex align-items-center justify-content-center"
                  style={{ width: '35px', height: '35px', marginRight: '10px' }}
                >
                  <BellOutlinedIcon className="text-primray-light" width={'18px'} height={'18px'} />
                </div>
                <div>
                  <div className="text-primary fs-4 fw-bold mb-1">SMS Notifications</div>
                  <div className="text-field-light">
                    Manage what you want to be notified about through SMS messages.
                  </div>
                </div>
              </div>

              <div
                className="mt-4 pt-3 pb-2"
                style={{
                  borderTop: '1px solid #e5e7eb',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <div className="fw-semibold text-field-mid">Notification Type</div>
              </div>

              <div className="mt-4">
                {[
                  {
                    key: 'gradmap',
                    title: 'GradMap Updates SMS Notifications',
                    desc:
                      'I agree to receive SMS update messages from GradMap to the phone number provided in my profile above. Message frequency may vary, data rates may apply, reply STOP or uncheck the box to opt out.',
                    source: 'gradmap',
                  },
                  {
                    key: 'in-app',
                    title: 'GradMap In-App Messaging SMS Notifications',
                    desc:
                      'I agree to receive SMS conversation messages (with other users) from GradMap to the phone number provided in my profile above. Message frequency may vary, data rates may apply, reply STOP or uncheck the box to opt out.',
                    source: 'in-app',
                  },
                ].map((item, idx) => {
                  const isInApp = item.source === 'in-app';
                  // HEADER-DOCS-024: In-App SMS state comes from the cam-mirror profile;
                  // if that endpoint failed, fall back to the stored notification array.
                  const inAppChecked =
                    smsProfile != null
                      ? smsProfile.smsReceiveEnabled
                      : getEmailNotificationStatus('sms', 'receive', item.source);
                  // Locked = not allowed by plan AND currently off. A user who already has
                  // SMS on can still switch it off (the backend only rejects turning it on).
                  const notAllowed =
                    smsProfileStatus === 'ok'
                      ? !!smsProfile && !smsProfile.smsAllowed
                      : smsProfileStatus === 'error' && isSmsLockedByPlan;
                  const isLocked = isInApp && notAllowed && !inAppChecked;
                  const openUpgrade = () => setUpgradeOpen(true);
                  return (
                    <div
                      key={item.key}
                      className={`d-flex align-items-start${isLocked ? ' sms-locked-row' : ''}`}
                      style={{
                        padding: '18px 4px',
                        borderBottom: idx === 0 ? '1px solid #eee' : 'none',
                        ...(isLocked ? { cursor: 'pointer', borderRadius: 6 } : {}),
                      }}
                      {...(isLocked
                        ? {
                            role: 'button',
                            tabIndex: 0,
                            'aria-label': `${item.title} — upgrade to ${SMS_MIN_PLAN_LABEL} to enable`,
                            onClick: openUpgrade,
                            onKeyDown: (e: React.KeyboardEvent) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openUpgrade();
                              }
                            },
                          }
                        : {})}
                    >
                      <div style={{ flex: 1, paddingRight: '12px', textAlign: 'left' }}>
                        <div className="fw-semibold text-field-mid d-flex align-items-center flex-wrap mb-2">
                          <SmartphoneOutlinedIcon fontSize="small" className="me-2 text-field-mid" />
                          {item.title}
                          {isLocked && (
                            <Chip
                              icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                              label={SMS_MIN_PLAN_LABEL}
                              size="small"
                              sx={{ ml: 1, fontSize: 11, height: 20 }}
                            />
                          )}
                        </div>
                        <div className="text-field-light" style={{ lineHeight: 1.6 }}>
                          {isLocked
                            ? `SMS for chat messages is available on ${SMS_MIN_PLAN_LABEL} and Gold plans. You'll still get chat messages in-app and by email.`
                            : item.desc}
                        </div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'flex-end',
                          textAlign: 'right',
                        }}
                      >
                        {isLocked ? (
                          <LockOutlinedIcon className="text-field-light" sx={{ mt: 1, mr: 1 }} />
                        ) : isInApp ? (
                          <Switch
                            checked={inAppChecked}
                            // Disabled while the SMS profile loads, so the row never flips
                            // from Switch to lock after the request resolves.
                            disabled={smsSaving || saving || smsProfileStatus === 'loading'}
                            inputProps={{ 'aria-label': 'Toggle SMS for in-app messages' }}
                            onChange={(_: any, checked: boolean) =>
                              smsProfile != null
                                ? saveInAppSms(checked)
                                : updateEmailNotificationStatus('sms', checked, 'receive', item.source)
                            }
                          />
                        ) : (
                          <Checkbox
                            checked={getEmailNotificationStatus('sms', 'receive', item.source)}
                            disabled={saving}
                            onChange={(_: any, checked: boolean) =>
                              updateEmailNotificationStatus('sms', checked, 'receive', item.source)
                            }
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Col>

          {/* Student Notifications Section */}
          {/* <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-success text-white">
              <div className="d-flex align-items-center">
                <SmartPhoneOutlinedIcon className="me-2" />
                <h5 className="mb-0">Student Notifications</h5>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <TableContainer component={'div'}>
                <Table sx={{ minWidth: 650 }} aria-label="student notifications table">
                  <TableHead>
                    <TableRow className="bg-light">
                      <TableCell className="fw-bold">Notification Type</TableCell>
                      <TableCell align="center" className="fs-16px fw-bold">
                        <SmartPhoneOutlinedIcon
                          fontSize="small"
                          className="text-field-light mr-4px"
                        />
                        Phone
                      </TableCell>
                      <TableCell align="center" className="fs-16px fw-bold">
                        <EmailOutlinedIcon fontSize="small" className="text-field-light mr-4px" />
                        Email
                      </TableCell>
                      <TableCell align="center" className="fs-16px fw-bold">
                        <BellOutlinedIcon
                          fontSize="small"
                          className="text-field-light mr-4px"
                        />
                        In-App
                      </TableCell>
                      <TableCell align="center" className="fs-16px fw-bold">
                        <BellOutlinedIcon
                          fontSize="small"
                          className="text-field-light mr-4px"
                        />
                        Push
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ backgroundColor: '#FCFBFC' }}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="text-field-mid fs-16px pl-30px"
                      >
                        Application Status
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getApplicationStatus(0)}
                          onChange={(_: any, checked: boolean) => updateApplicationStatus(0, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getApplicationStatus(1)}
                          onChange={(_: any, checked: boolean) => updateApplicationStatus(1, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getApplicationStatus(2)}
                          onChange={(_: any, checked: boolean) => updateApplicationStatus(2, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getApplicationStatus(3)}
                          onChange={(_: any, checked: boolean) => updateApplicationStatus(3, checked)}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        className="text-field-mid fs-16px pl-30px"
                      >
                        News & Updates
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getNewsStatus(0)}
                          onChange={(_, checked) => updateNewsStatus(0, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getNewsStatus(1)}
                          onChange={(_, checked) => updateNewsStatus(1, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getNewsStatus(2)}
                          onChange={(_, checked) => updateNewsStatus(2, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getNewsStatus(3)}
                          onChange={(_, checked) => updateNewsStatus(3, checked)}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: '#FCFBFC' }}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="text-field-mid fs-16px pl-30px"
                      >
                        Special Dates
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getSpecialDatesStatus(0)}
                          onChange={(_, checked) => updateSpecialDatesStatus(0, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getSpecialDatesStatus(1)}
                          onChange={(_, checked) => updateSpecialDatesStatus(1, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getSpecialDatesStatus(2)}
                          onChange={(_, checked) => updateSpecialDatesStatus(2, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getSpecialDatesStatus(3)}
                          onChange={(_, checked) => updateSpecialDatesStatus(3, checked)}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        className="text-field-mid fs-16px pl-30px"
                      >
                        College Updates
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getCollegeUpdatesStatus(0)}
                          onChange={(_, checked) => updateCollegeUpdatesStatus(0, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getCollegeUpdatesStatus(1)}
                          onChange={(_, checked) => updateCollegeUpdatesStatus(1, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getCollegeUpdatesStatus(2)}
                          onChange={(_, checked) => updateCollegeUpdatesStatus(2, checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={getCollegeUpdatesStatus(3)}
                          onChange={(_, checked) => updateCollegeUpdatesStatus(3, checked)}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card.Body>
          </Card>
        </Col> */}
        </Row>

        {/* HEADER-DOCS-024: upgrade CTA for the locked In-App Messaging SMS row */}
        <Dialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>SMS for chat messages is a paid feature</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Upgrade to {SMS_MIN_PLAN_LABEL} or Gold to get a text whenever someone messages you,
              on top of your in-app and email notifications.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUpgradeOpen(false)}>Not now</Button>
            <Button variant="contained" onClick={goToPlans}>
              See plans
            </Button>
          </DialogActions>
        </Dialog>

        <style scoped>{`
        th, td {
          border: none !important;
        }
        .sms-locked-row:hover {
          background-color: rgba(0, 0, 0, 0.04);
        }
        .sms-locked-row:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(62, 115, 240, 0.35);
        }
        .card-header {
          border-bottom: 1px solid rgba(0,0,0,.125);
        }
      `}</style>
      </div>
    </div>
  ) : (
    <div className="tab-wrap-panel">
      <div className="tab-panel2">
        <div className="tab-panel2-content">
          <Row className="m-0 p-0">
            <Col md={12} className="text-center">
              <h3>Loading...</h3>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default SettingsNotification;
